import {
    useEffect,
    useRef,
} from 'react';
import { optimizerErrorMessage } from '../components/optimizer/optimizerPortfolioMutationContract';
import {
    OPTIMIZER_SAVED_RECEIPT_CONTRACTS,
    fetchOptimizerV2PortfolioDetail,
} from '../utils/optimizerGenericGroupContract';

import { savedOptimizerRunConfiguration } from '../utils/optimizerRunCommand';

export function useLoadSavedOptimizedPortfolio({
    whatIfPortfolio,
    watchlistVersion = 0,
    unsavedOptimizerRun,
    listOfWhatIfPortfolios,
    setOptimizerIsRunning,
    installSavedOptimizerConfiguration,
    acceptSavedResult,
    optimizerAttributeCatalog,
    resetOptimizerDefaults,
    setOptStatus,
    setErrorOptimization,
}) {
    const selectedSavedPortfolio = listOfWhatIfPortfolios.find((portfolio) => (
        portfolio?.value === whatIfPortfolio
    ));
    const hasSelectedSavedPortfolio = selectedSavedPortfolio !== undefined;
    const selectedSavedPortfolioId = selectedSavedPortfolio?.id ?? null;
    const selectedSavedPortfolioReceiptStatus = selectedSavedPortfolio?.receiptStatus ?? null;
    const selectedSavedPortfolioVersion = selectedSavedPortfolio?.portfolioVersion ?? null;
    const selectedCompatibility = selectedSavedPortfolio?.compatibility ?? null;
    const latestActionsRef = useRef(null);
    const lastLoadSelectionKeyRef = useRef('');
    latestActionsRef.current = {
        installSavedOptimizerConfiguration,
        acceptSavedResult,
        optimizerAttributeCatalog,
        resetOptimizerDefaults,
        setErrorOptimization,
        setOptimizerIsRunning,
        setOptStatus,
    };
    const selectionKey = [
        String(watchlistVersion),
        String(selectedSavedPortfolioId ?? ''),
        String(selectedSavedPortfolioVersion ?? ''),
        String(selectedSavedPortfolioReceiptStatus ?? ''),
        String(selectedCompatibility?.status ?? ''),
        String(selectedCompatibility?.current_watchlist_revision_id ?? ''),
        String(selectedCompatibility?.available_security_count ?? ''),
        String(selectedCompatibility?.missing_security_count ?? ''),
    ].join('|');

    const installDetail = (detail, actions) => {
        const sourceContract = detail.source_receipt_contract;
        if (!OPTIMIZER_SAVED_RECEIPT_CONTRACTS.includes(sourceContract)) {
            throw new Error('Saved optimizer receipt contract is unsupported.');
        }
        // Recheck the detail response: the Watchlist may have changed after
        // the dropdown's selection preflight. Never install a partial portfolio.
        if (detail.compatibility.missing_security_count > 0) {
            throw new Error('Restore the missing securities from the saved portfolio’s details before loading it.');
        }
        const configuration = savedOptimizerRunConfiguration(detail, selectedSavedPortfolio, {
            catalog: actions.optimizerAttributeCatalog,
        });
        actions.installSavedOptimizerConfiguration(configuration,
            { allocations: detail.allocations, securityRows: detail.securityRows });
        actions.acceptSavedResult(detail);
        actions.setOptStatus('SUCCESS');
    };

    useEffect(() => {
        if (!whatIfPortfolio || unsavedOptimizerRun || !hasSelectedSavedPortfolio) {
            lastLoadSelectionKeyRef.current = '';
            return undefined;
        }
        if (lastLoadSelectionKeyRef.current === selectionKey) return undefined;
        lastLoadSelectionKeyRef.current = selectionKey;

        let cancelled = false;

        async function loadSaved() {
            const actions = latestActionsRef.current;
            actions.resetOptimizerDefaults();
            actions.setOptimizerIsRunning(true);
            actions.setErrorOptimization(null);

            try {
                if (!selectedSavedPortfolioId) {
                    actions.setErrorOptimization('Selected portfolio is missing an id.');
                    return;
                }
                if (
                    selectedSavedPortfolioReceiptStatus !== 'available'
                ) {
                    actions.setErrorOptimization(
                        'This saved optimizer portfolio is unavailable.',
                    );
                    return;
                }
                const detail = await fetchOptimizerV2PortfolioDetail(
                    selectedSavedPortfolioId,
                    selectedSavedPortfolioVersion,
                );
                if (cancelled) return;
                if (detail.receipt_status !== 'available') {
                    actions.setErrorOptimization(
                        'This saved optimizer portfolio is unavailable.',
                    );
                    return;
                }
                installDetail(detail, latestActionsRef.current);
            } catch (error) {
                if (!cancelled) {
                    latestActionsRef.current.setErrorOptimization(
                        `Failed to load saved optimizer portfolio. ${optimizerErrorMessage(
                            error,
                            'Optimizer portfolio detail is unavailable.',
                        )}`,
                    );
                }
            } finally {
                if (!cancelled) {
                    latestActionsRef.current.setOptimizerIsRunning(false);
                }
            }
        }

        void loadSaved();

        return () => {
            cancelled = true;
        };
    }, [
        whatIfPortfolio,
        unsavedOptimizerRun,
        hasSelectedSavedPortfolio,
        selectedSavedPortfolioId,
        selectedSavedPortfolioReceiptStatus,
        selectedSavedPortfolioVersion,
        selectionKey,
    ]);
}
